import { useCallback, useEffect, useState } from 'react';
import { Descendant, Editor } from 'slate';
import { ReactEditor } from 'slate-react';

import { bulletinboardsApi } from 'apis/bulletinboards';
import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { deserializeFromHtml } from '../components/richText/utils/deserializeFromHtml';
import { EmptyDocument } from '../components/richText/utils/editorUtils';
import { serializeToHtml } from '../components/richText/utils/serializeToHtml';

import { useLoadBulletin } from './useLoadBulletin';

/**
 * The view mode of the editor describes if we are currently viewing the
 * saved/published version or the currently made edits
 */
export type ViewMode = 'editor' | 'saved';

type UseBulletinReturn = {
  /** Bulletin content in slate.js format, with edits if there are any */
  content: Descendant[];
  /** Function to revert all changes made */
  discardChanges: () => void;
  /** Function to call to navigate back to messenger settings */
  goBackToMessengerSettings: () => void;
  /** Flag indicating if changes have been made */
  hasChanges: boolean;
  /** Flag indicating fetching has errored */
  hasLoadError: boolean;
  /** The initial bulletin content */
  initialContent: Descendant[];
  /** The initial bulletin title */
  initialTitle: string;
  /** Is the bulletin currently active */
  isActive: boolean;
  /** Are we fetching the bulletin */
  isFetching: boolean;
  /** Is the bulletin saving */
  isUpdating: boolean;
  /** Function to trigger a save of the bulletin */
  saveChanges: () => Promise<void>;
  /** Function to update bulletin content, is slate.js format */
  setContent: (newContent: Descendant[]) => void;
  /** Function to update bulletin title */
  setTitle: (newTitle: string) => void;
  /** Function to update active view mode */
  setViewMode: (viewMode: ViewMode) => void;
  /** Bulletin title, with edits if there are any */
  title: string;
  /** Current view mode */
  viewMode: ViewMode;
};

/**
 * Keeps track of the initial and edited states of the bulletin.
 *
 * @param param0 Data required to load the correct bulletin
 */
export function useBulletinEditor({
  messengerId,
  bulletinId,
  editor,
}: {
  messengerId: string;
  bulletinId: string;
  editor: Editor;
}): UseBulletinReturn {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const notify = useNotifications();

  const [viewMode, setViewModeInternal] = useState<ViewMode>('editor');
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<Descendant[]>(EmptyDocument);
  const [initialContent, setinitialContent] =
    useState<Descendant[]>(EmptyDocument);

  const [updateBulletin, { isLoading: isUpdating }] =
    bulletinboardsApi.useUpdateBulletinMutation();

  const notifyError = useCallback(
    (message: string) => notify({ title: message, type: 'error' }),
    [notify],
  );

  const notifySuccess = useCallback(
    (message: string) => notify({ title: message, type: 'success' }),
    [notify],
  );

  const setViewMode = (mode: ViewMode) => {
    // Value in slate is now only initialState, so change the editors document
    // to either the current work in progress or the unedited document 'initialState'
    editor.children = mode === 'editor' ? content : initialContent;
    setViewModeInternal(mode);
  };

  const {
    isFetching,
    bulletinBoardId,
    bulletin,
    hasError: hasLoadError,
  } = useLoadBulletin({
    messengerId,
    bulletinId,
  });

  useEffect(() => {
    if (hasLoadError) {
      notifyError(
        'Sorry, there has been an problem loading the bulletin, please try again.',
      );
    }
  }, [notifyError, hasLoadError]);

  const isActive = bulletin?.active || false;

  const resetChanges = () => {
    // Deselect the editor so that the toolbar buttons don't try to update "active" state
    // while document is updating, which causes a crash.
    ReactEditor.deselect(editor);
    editor.children = initialContent;
    setContent(initialContent);
    setViewModeInternal('editor');
  };

  useEffect(() => {
    if (!bulletin) return;
    const serializedContent = deserializeFromHtml(bulletin.content);
    setContent(serializedContent);
    editor.children = serializedContent;
    setinitialContent(serializedContent);
    setTitle(bulletin.title);
  }, [bulletin, editor]);

  const hasChanges =
    !isFetching && (content !== initialContent || title !== bulletin?.title);

  const saveChanges = async () => {
    if (!bulletinBoardId) return;

    // Deselect the editor so that the toolbar buttons don't try to update "active" state
    // while document is updating, which causes a crash.
    ReactEditor.deselect(editor);

    const convertedContent = serializeToHtml(content);
    try {
      await updateBulletin({
        boardId: bulletinBoardId,
        bulletinId,
        update: {
          content: convertedContent,
          title,
        },
      });
      const noteTitle = isActive
        ? 'Your content has been published. Nice!'
        : 'Your content has been saved.';
      notifySuccess(noteTitle);
    } catch (error) {
      notifyError(
        'Sorry, there has been an error saving your changes. Please try again.',
      );
    }
  };

  const goBackToMessengerSettings = async () => {
    if (hasChanges) {
      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: 'Unsaved changes',
              description:
                'Are you sure you want to leave? You have unsaved changes, if you leave you will loose your changes.',
              confirm: {
                label: 'Yes, leave',
              },
            },
          },
        }),
      );

      if (!isConfirmed) return;
    }

    navigate({
      path: '/settings/dixa-messenger/settings/:messengerId',
      params: { path: { messengerId } },
    });
  };

  return {
    content,
    discardChanges: resetChanges,
    goBackToMessengerSettings,
    hasChanges,
    hasLoadError,
    initialContent,
    initialTitle: bulletin?.title || '',
    isActive,
    isFetching,
    isUpdating,
    saveChanges,
    setContent,
    setTitle,
    setViewMode,
    title,
    viewMode,
  };
}
